import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { TooltipButtonComponent } from '@app/core/components/tooltip-button/tooltip-button.component';
import { HourglassSpinnerComponent } from '@shared/hourglass-spinner/hourglass-spinner.component';
import { GuidedCourseCreationService } from '@services/guided-course-creation.service';
import {
  MESSAGE_TYPES,
  ChatMessage,
  MessageType,
  OutlineApiResponse,
  getChatErrorKey,
  CHAT_ERROR_KEYS,
} from '@core/constants/guided-course-creation.constants';

export { MESSAGE_TYPES, ChatMessage, MessageType };

@Component({
  selector: 'app-outline-chat',
  templateUrl: './outline-chat.component.html',
  styleUrls: ['./outline-chat.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    TranslateModule,
    TooltipButtonComponent,
    HourglassSpinnerComponent,
  ],
})
export class OutlineChatComponent {
  readonly MESSAGE_TYPES = MESSAGE_TYPES;

  readonly isLoading = input(false);
  readonly sessionId = input.required<string>();
  readonly sectionsLoaded = input(false);

  readonly outlineUpdated = output<OutlineApiResponse>();

  readonly messages = signal<ChatMessage[]>([]);
  readonly pendingPreview = signal<ChatMessage | null>(null);
  readonly isFetching = signal(false);
  private readonly pendingIntroMessage = signal<string | null>(null);
  readonly isInputDisabled = computed(
    () =>
      this.isLoading() ||
      this.isFetching() ||
      !!this.pendingPreview() ||
      this.messageForm.invalid,
  );

  private readonly messagesContainer =
    viewChild<ElementRef<HTMLElement>>('messagesContainer');

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gccService = inject(GuidedCourseCreationService);
  private readonly translateService = inject(TranslateService);

  readonly messageForm = this.fb.nonNullable.group({
    content: ['', Validators.required],
  });

  constructor() {
    effect(() => {
      this.messages();
      this.pendingPreview();
      this.scrollToBottom();
    });

    effect(() => {
      const method = this.isInputDisabled() ? 'disable' : 'enable';
      this.messageForm.controls.content[method]();
    });

    // Fetch assistant introduction when sessionId is available
    effect(() => {
      const sid = this.sessionId();
      if (sid) {
        this.fetchIntroduction(sid);
      }
    });

    // Display intro message once sections are loaded
    effect(() => {
      const intro = this.pendingIntroMessage();
      if (intro && this.sectionsLoaded()) {
        this.messages.update(msgs => [
          ...msgs,
          { type: MESSAGE_TYPES.AI, content: intro },
        ]);
        this.pendingIntroMessage.set(null);
      }
    });
  }

  sendMessage(): void {
    if (
      this.messageForm.invalid ||
      this.isLoading() ||
      this.isFetching() ||
      this.pendingPreview()
    )
      return;

    const content = this.messageForm.getRawValue().content.trim();
    if (!content) return;

    this.messages.update(msgs => [
      ...msgs,
      { type: MESSAGE_TYPES.USER, content },
    ]);
    this.messageForm.reset();

    this.isFetching.set(true);
    this.gccService
      .sendChatMessage(this.sessionId(), content)
      .pipe(
        finalize(() => this.isFetching.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          if (response.pending_actions) {
            this.pendingPreview.set({
              type: MESSAGE_TYPES.PREVIEW,
              content: response.message,
              summary: response.summary,
              pendingActions: true,
            });
          } else {
            this.messages.update(msgs => [
              ...msgs,
              { type: MESSAGE_TYPES.AI, content: response.message },
            ]);
          }
        },
        error: (err: HttpErrorResponse) => this.handleError(err),
      });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  confirmPreview(): void {
    const preview = this.pendingPreview();
    if (!preview || this.isFetching()) return;

    this.pendingPreview.set(null);
    this.isFetching.set(true);
    this.gccService
      .confirmActions(this.sessionId())
      .pipe(
        finalize(() => this.isFetching.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.messages.update(msgs => [
            ...msgs,
            { type: MESSAGE_TYPES.AI, content: response.message },
          ]);
          if (response.outline) {
            this.outlineUpdated.emit(response.outline);
          }
        },
        error: () =>
          this.addErrorMessage(
            undefined,
            'guidedCourseCreation.chat.errorConfirm',
          ),
      });
  }

  cancelPreview(): void {
    const preview = this.pendingPreview();
    if (!preview || this.isFetching()) return;

    this.pendingPreview.set(null);
    this.isFetching.set(true);
    this.gccService
      .cancelActions(this.sessionId())
      .pipe(
        finalize(() => this.isFetching.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.messages.update(msgs => [
            ...msgs,
            { type: MESSAGE_TYPES.AI, content: response.message },
          ]);
        },
        error: (err: HttpErrorResponse) => this.handleError(err),
      });
  }

  private handleError(err: HttpErrorResponse): void {
    if (err.status === 500) {
      this.restartAfterError();
    } else {
      this.addErrorMessage(err);
    }
  }

  private fetchIntroduction(sessionId: string): void {
    this.isFetching.set(true);
    this.gccService
      .assistantIntroduction(sessionId)
      .pipe(
        finalize(() => this.isFetching.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => this.pendingIntroMessage.set(response.message),
        error: () => {},
      });
  }

  private restartAfterError(): void {
    const sid = this.sessionId();
    if (!sid) {
      this.addErrorMessage();
      return;
    }

    this.isFetching.set(true);
    this.gccService
      .restartChat(sid)
      .pipe(
        finalize(() => this.isFetching.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.messages.set([
            { type: MESSAGE_TYPES.AI, content: response.message },
          ]);
          this.pendingPreview.set(null);
        },
        error: () => {
          this.addErrorMessage();
        },
      });
  }

  private addErrorMessage(err?: HttpErrorResponse, overrideKey?: string): void {
    const key =
      overrideKey ??
      (err ? getChatErrorKey(err.status) : CHAT_ERROR_KEYS.GENERIC);
    this.messages.update(msgs => [
      ...msgs,
      {
        type: MESSAGE_TYPES.AI,
        content: this.translateService.instant(key),
        isError: true,
      },
    ]);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const container = this.messagesContainer()?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }
}
